import { autoinject, newInstance } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { GenericApi } from '~/api/generic-api';
import { LoginConstants } from '~/login/login-constants';
import {
	FetchUtils,
	HttpMethod,
	OAuthErrorResponse,
} from '~/utils/fetch-utils';
import { getItem, removeItem, setItem } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';

import URI from 'urijs';

export interface OAuthToken {
	access_token: string;
	expires_in: number;
	refresh_token: string;
	scope: string; // ie. "read write trust"
	token_type: string; // ie. "bearer"
}

@autoinject
export class LoginApi {
	private static readonly CLIENT_ID = 'acurity-web-trusted-client';
	public static readonly TOKEN_ENDPOINT = 'oauth/token';

	private isLoggingOut: boolean = false;

	constructor(private readonly i18n: I18N) {}

	public login(username: string, password: string): Promise<OAuthToken> {
		const loginParams = new URLSearchParams({
			grant_type: 'password',
			client_id: LoginApi.CLIENT_ID,
			username,
			password,
		});

		// append the client id and secret in the request URL as parameter to get the access token
		// client id and secret must match with backend client security data
		// username and password must be in form data for security issue.
		return FetchUtils.fetchJSON<
			OAuthToken & OAuthErrorResponse,
			URLSearchParams
		>(HttpMethod.POST, LoginApi.TOKEN_ENDPOINT, undefined, loginParams, {
			disableInterceptors: true,
		}).then((response): OAuthToken => {
			if (FetchUtils.isError(response)) {
				let reason: string = '';
				switch (response.error) {
					case LoginConstants.INVALID_CLIENT:
						reason = this.i18n.tr(
							'errors.client-authentication-failed--exact',
						);
						break;
					case LoginConstants.INVALID_GRANT:
						reason = this.i18n.tr(
							'errors.invalid-username-or-password',
						);
						break;
					default:
						reason = FetchUtils.normalizeError(response);
						break;
				}
				throw new Error(
					this.i18n.tr('errors.login-failed', { reason }),
				);
			}
			return LoginApi.storeTokenResponse(response);
		});
	}

	/**
	 * Get access token
	 * @returns {string} Access token
	 */
	public static getAccessToken(): string | null {
		return getItem(LoginConstants.ACCESS_TOKEN);
	}

	/**
	 * Tell the server that we have logged out
	 * Note: Check access token existence first before calling this API
	 */
	public logout(): Promise<void> {
		if (this.isLoggingOut) {
			return Promise.resolve();
		}
		const url = GenericApi.apiServerURL + LoginApi.TOKEN_ENDPOINT;
		this.isLoggingOut = true;
		return FetchUtils.fetchJSON(HttpMethod.DELETE, url, {
			client_id: LoginApi.CLIENT_ID,
		})
			.then(() => {
				removeItem(LoginConstants.ACCESS_TOKEN);
				removeItem(LoginConstants.REFRESH_TOKEN);
				return;
			})
			.catch((err) => {
				const errorMessage: string = 'Failed to clear the access token';
				Logger.error(errorMessage, err);
			})
			.finally(() => {
				this.isLoggingOut = false;
			});
	}

	public isRefreshRequest(url: string): boolean {
		const uri = URI(url);
		return (
			uri.path() === LoginApi.TOKEN_ENDPOINT &&
			uri.hasQuery('grant_type', 'refresh_token') &&
			uri.hasQuery('refresh_token')
		);
	}

	/**
	 * Refresh Access Token
	 */
	public refreshAccessToken(
		refreshToken: string,
		initiator: string,
		params: { [paramName: string]: any },
	): Promise<OAuthToken> {
		const loginParams = new URLSearchParams({
			grant_type: 'refresh_token',
			client_id: LoginApi.CLIENT_ID,
			refresh_token: refreshToken,
			initiator,
			...params,
		});
		return FetchUtils.fetchJSON<OAuthToken & OAuthErrorResponse>(
			HttpMethod.POST,
			LoginApi.TOKEN_ENDPOINT,
			{},
			loginParams,
			{ disableInterceptors: true },
		).then((response): OAuthToken => {
			if (FetchUtils.isError(response)) {
				let reason: string = '';
				switch (response.error) {
					case LoginConstants.INVALID_GRANT:
						reason = this.i18n.tr(
							response.error_description.indexOf('client') !== -1
								? 'errors.wrong-client--exact'
								: 'errors.invalid-refresh-token',
						);
						break;
					case LoginConstants.INVALID_TOKEN:
						// refresh toke expired tells us access token and refresh token are already remove from remote token store
						// remove them from localStorage so that we don't fire a request to delete remote access token
						// see `invalidateLogin` method in app.ts
						removeItem(LoginConstants.ACCESS_TOKEN);
						removeItem(LoginConstants.REFRESH_TOKEN);
						reason = this.i18n.tr(
							'errors.invalid-refresh-token-expired',
						);
						break;
					default:
						reason = FetchUtils.normalizeError(response);
						break;
				}
				throw new Error(
					this.i18n.tr('errors.refresh-access-token-failed', {
						reason,
					}),
				);
			}
			return LoginApi.storeTokenResponse(response);
		});
	}

	/**
	 * storeTokenResponse is used by this class and Cypress unit tests
	 */
	public static storeTokenResponse(response: OAuthToken): OAuthToken {
		const now = new Date();
		now.setSeconds(now.getSeconds() + response.expires_in);

		setItem(LoginConstants.EXPIRE_TIME, now.toString());
		setItem(LoginConstants.ACCESS_TOKEN, response.access_token);
		setItem(LoginConstants.REFRESH_TOKEN, response.refresh_token);
		setItem(LoginConstants.REFRESHING_TOKEN, false);
		return response;
	}
}
